import { useState, type FormEvent, type ReactNode } from "react";

const topics = ["Individual", "Couples", "Family / Custody", "Juvenile", "LGBTQ+", "Other"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FieldName = "name" | "email" | "phone" | "topic" | "message" | "consent";
type Errors = Partial<Record<FieldName, string>>;

function validate(form: HTMLFormElement): { ok: true; body: Record<string, string> } | { ok: false; errors: Errors } {
  const data = new FormData(form);
  const name = String(data.get("name") ?? "").trim();
  const email = String(data.get("email") ?? "").trim();
  const phone = String(data.get("phone") ?? "").trim();
  const topic = String(data.get("topic") ?? "").trim();
  const message = String(data.get("message") ?? "").trim();
  const consent = data.get("consent") === "on";

  const errors: Errors = {};
  if (name.length < 2 || name.length > 80) errors.name = "Please share your name";
  if (!EMAIL_RE.test(email) || email.length > 160) errors.email = "A valid email helps Jerry reach you";
  if (phone.length > 40) errors.phone = "Please use a shorter phone number";
  if (topic.length > 60) errors.topic = "Please choose a topic";
  if (message.length < 10) errors.message = "A few sentences help Jerry prepare";
  else if (message.length > 2000) errors.message = "Please keep it under 2000 characters";
  if (!consent) errors.consent = "Please confirm so we can reply";

  if (Object.keys(errors).length) return { ok: false, errors };
  return { ok: true, body: { name, email, phone, topic, message } };
}

export function Contact() {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = validate(event.currentTarget);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    setErrorMsg(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(result.body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to send. Please try again.");
      }
      setSent(true);
      event.currentTarget.reset();
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong. Please call or text instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="relative overflow-hidden rounded-3xl border border-border bg-card shadow-[var(--shadow-lift)]">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-20 -top-20 hidden h-80 w-80 rounded-full bg-sage/30 blur-3xl md:block"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -left-16 hidden h-72 w-72 rounded-full bg-clay/20 blur-3xl md:block"
          />
          <div className="relative grid gap-10 p-8 sm:p-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <div className="reveal">
              <p className="text-xs font-medium uppercase tracking-[0.22em] text-sage-deep">
                Get in touch
              </p>
              <h2 className="mt-4 font-display text-4xl font-medium tracking-tight text-balance sm:text-5xl">
                Start with a quiet conversation.
              </h2>
              <p className="mt-5 text-foreground/75">
                Tell Jerry a little about what's going on. He'll respond personally — by phone or
                email — to set up a free intro.
              </p>
              <dl className="mt-10 space-y-5 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                    Call or text
                  </dt>
                  <dd className="mt-1">
                    <a className="font-display text-2xl text-foreground hover:text-sage-deep" href="tel:5075302029">
                      (507) 530‑2029
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email</dt>
                  <dd className="mt-1">
                    <a className="font-display text-xl text-foreground hover:text-sage-deep" href="mailto:jrbrmb123@yahoo.com">
                      jrbrmb123@yahoo.com
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Office</dt>
                  <dd className="mt-1">110 East Second Street, Minnesota</dd>
                </div>
              </dl>
            </div>

            <form
              onSubmit={onSubmit}
              className="reveal space-y-5"
              noValidate
              aria-label="Contact form"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Your name" error={errors.name}>
                  <input name="name" className="input" autoComplete="name" placeholder="Alex Morgan" />
                </Field>
                <Field label="Email" error={errors.email}>
                  <input
                    name="email"
                    type="email"
                    className="input"
                    autoComplete="email"
                    placeholder="you@example.com"
                  />
                </Field>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Phone (optional)" error={errors.phone}>
                  <input
                    name="phone"
                    type="tel"
                    className="input"
                    autoComplete="tel"
                    placeholder="(555) 000‑0000"
                  />
                </Field>
                <Field label="What's this about?" error={errors.topic}>
                  <select name="topic" className="input bg-card" defaultValue="">
                    <option value="">Choose a topic</option>
                    {topics.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Message" error={errors.message}>
                <textarea
                  name="message"
                  rows={5}
                  className="input resize-none"
                  placeholder="A few sentences about what's going on — only what you're comfortable sharing."
                />
              </Field>
              <label className="flex min-h-12 cursor-pointer items-start gap-3 py-1 text-sm text-foreground/75">
                <input
                  type="checkbox"
                  name="consent"
                  className="mt-1 h-5 w-5 shrink-0 rounded border-border accent-[color:var(--sage-deep)]"
                />
                <span>
                  I understand this form sends a message to Jerry and isn't a substitute for crisis
                  care. If you're in immediate danger, please call 988 or 911.
                </span>
              </label>
              {errors.consent && (
                <p className="-mt-3 text-xs text-destructive">{errors.consent}</p>
              )}
              {errorMsg && (
                <p className="-mt-3 text-xs text-destructive">{errorMsg}</p>
              )}
              <button
                type="submit"
                disabled={submitting || sent}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-4 text-base font-medium text-primary-foreground shadow-[var(--shadow-lift)] transition-transform hover:-translate-y-0.5 disabled:opacity-70 sm:w-auto"
              >
                {sent ? "Thanks — Jerry will be in touch soon." : submitting ? "Sending…" : "Send to Jerry"}
                <span aria-hidden>→</span>
              </button>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.875rem;
          border: 1px solid var(--color-border);
          background: color-mix(in oklab, var(--cream) 70%, white);
          padding: 0.85rem 1rem;
          font-size: 0.95rem;
          color: var(--color-foreground);
          transition: border-color .2s, box-shadow .2s, background .2s;
        }
        .input:focus {
          outline: none;
          border-color: var(--sage-deep);
          box-shadow: 0 0 0 4px color-mix(in oklab, var(--sage-deep) 18%, transparent);
          background: white;
        }
      `}</style>
    </section>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-destructive">{error}</span>}
    </label>
  );
}
